import { lazy, memo, Suspense, useMemo } from 'react';
import { selectShowScrensharePreview } from 'features/layout/features/config/configSlice';
import LocalScreensharePreviewOverlay from 'features/streaming/components/media/LocalScreensharePreviewOverlay';
import LocalVideoStream from 'features/streaming/components/media/LocalVideoStream';
import StreamMedia from 'features/streaming/components/stream-display/StreamMedia';
import { StreamingMediaProps } from 'features/streaming/types';
import { useAppSelector } from 'store/hooks';
import { usePiPWindow } from 'components/PiP/PiPProvider';
import { useIsShareEntireScreen } from 'features/streaming/hooks/useIsShareEntireScreen';
import {
  selectLocalScreenshareBroadcasting,
  selectLocalScreenshareStreams,
} from 'features/streaming/streamingSlice';
import { selectContentWidth } from 'features/layout/features/contentDimensions/contentDimensionsSlice';
import { userAgentDetails } from 'utils/userAgentDetails';
import { useContentWhiteboard } from 'features/content-whiteboard/useContentWhiteboard';
import { selectShowAllScreens } from 'features/screenshare/screenshareSlice';
import { useContentBoardPanelControl } from 'features/content-whiteboard/hooks/useContentBoardPanelControl';
import { useSidePanel } from 'features/side-panels/hooks/useSidePanel';
import { useWindowSize } from 'hooks/useWindowSize';
import { styleConstants } from 'theme/styleConstants';

const ScreenshareWhiteboardViewer = lazy(
  () => import('features/content-whiteboard/components/ScreenshareWhiteboardViewer')
);

const LocalScreenshare = memo(({ source, avatarSize }: StreamingMediaProps) => {
  const showScreensharePreview = useAppSelector(selectShowScrensharePreview);
  const { sidePanel } = useSidePanel();

  const streams = useAppSelector(selectLocalScreenshareStreams);
  const videoBroadcasting = useAppSelector(selectLocalScreenshareBroadcasting);
  const showAllScreens = useAppSelector(selectShowAllScreens);
  const { controlEnabled: contentBoardPanelEnabled } = useContentBoardPanelControl();

  const contentWidth = useAppSelector(selectContentWidth);
  const { pipWindow } = usePiPWindow();
  const { width: windowWidth } = useWindowSize();

  const contentWidthPx = sidePanel
    ? contentWidth * (windowWidth - styleConstants.toolbarPanel.width)
    : contentWidth * windowWidth;

  const wrapBarContent =
    !!pipWindow ||
    showAllScreens ||
    (!contentBoardPanelEnabled && contentWidthPx < 700) ||
    (contentBoardPanelEnabled && contentWidthPx < 800);

  const isShareEntireScreen = useIsShareEntireScreen();

  const { browser } = userAgentDetails();

  const { isFeatureAvailable } = useContentWhiteboard();

  const isFirefox = browser.name?.toLowerCase() === 'firefox';

  const previewPosition = useMemo(() => {
    if (contentBoardPanelEnabled) {
      if (contentWidthPx < 600) {
        return 'left';
      }

      return 'center';
    }

    return 'right';
  }, [contentBoardPanelEnabled, contentWidthPx]);

  if (!showScreensharePreview && (isShareEntireScreen || isFirefox)) {
    return <LocalScreensharePreviewOverlay wrap={wrapBarContent} />;
  }

  return (
    <>
      <StreamMedia source={source} avatarSize={avatarSize} hideAvatar>
        {streams.map((stream) =>
          stream.kind === 'video' && videoBroadcasting ? (
            <LocalVideoStream mode="screenshare" id={stream.id} key={stream.id} />
          ) : null
        )}
        {!showAllScreens && isFeatureAvailable && source.feedId && (
          <Suspense fallback={null}>
            <ScreenshareWhiteboardViewer screenshareId={source.feedId} />
          </Suspense>
        )}
      </StreamMedia>
      {(isShareEntireScreen || isFirefox) && (
        <LocalScreensharePreviewOverlay wrap={wrapBarContent} position={previewPosition} floating />
      )}
    </>
  );
});

export default LocalScreenshare;
